<template>
	<b-card-text>
		<b-row>
			<b-col cols="6" class="pb-3">
				<b-input-group>
					<b-form-input v-model="filter" type="text" :placeholder="FormMSG(31, 'Type to Search')" />
					<b-input-group-append class="cursor-pointer">
						<b-input-group-text class="btn-search">
							<component
								:is="getLucideIcon(ICONS.SEARCH.name)"
								:color="ICONS.SEARCH.color"
								:size="16"
								class="icon"
								:stroke-width="2.5"
								v-if="filter.length === 0"
							/>
							<component
								:is="getLucideIcon(ICONS.X.name)"
								:color="ICONS.X.color"
								:size="16"
								class="icon"
								:stroke-width="2.5"
								@click="filter = ''"
								v-else
							/>
						</b-input-group-text>
					</b-input-group-append>
				</b-input-group>
			</b-col>
		</b-row>
		<div v-if="$screen.width <= 992" class="container-mobile">
			<PoDrawDownDataMobile
				@selectAll="setSelectionForAll"
				:allSelected="isSelectedAll"
				:selectedDataRows="selectedRows"
				:poDataLists="poLists"
				@updateSelectedRows="updateSelectedRows"
			/>
		</div>
		<b-table
			v-else
			id="table-clickable"
			:hover="hover"
			responsive="sm"
			:filter="filter"
			:items="poLists"
			:fields="poFields"
			:current-page="currentPage"
			:per-page="0"
			bordered
			outlined
			small
			sticky-header="700px"
			:head-row-variant="hv"
			:empty-text="FormMSG(10, 'No POs found')"
			show-empty
		>
			<template #head(select)="">
				<b-form-checkbox size="lg" v-model="isSelectedAll" @change="setSelectionForAll" />
			</template>
			<template #cell(select)="data">
				<span>
					<b-form-checkbox size="lg" class="pj-cb pb-1" v-model="data.item.selectState" @change="updateSelectedRows($event, data.item)" />
				</span>
			</template>
			<template #cell(validatedStatus)="data">
				<div class="wrap-status">
					<div :class="`status  ${data.item.statusClass}`" style="font-size: 0.7rem">
						{{ data.item.validatedStatus }}
					</div>
				</div>
			</template>
		</b-table>
	</b-card-text>
</template>

<script>
// utils
import { rendCurrency, formatTableDate } from '~helpers';
import { VALIDATION_TYPE } from '@/shared/constants';

// mixin
import globalMixin from '@/mixins/global.mixin';
import languageMessages from '@/mixins/languageMessages';

// props
import mapProps from '@/shared/vuePropsMapper';
import PoDrawDownDataMobile from './PoDrawDownDataMobile.vue';
// cruds
import { getProjectUserPoDetail } from '@/cruds/budget.crud';

export default {
	name: 'PoDrawDownDataView',
	mixins: [languageMessages, globalMixin],
	components: {
		PoDrawDownDataMobile
	},
	props: {
		...mapProps(['hover', 'striped', 'bordered'], {
			type: Boolean,
			default: true
		}),
		...mapProps(['small', 'fixed'], {
			type: Boolean,
			default: false
		}),
		selectedPo: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			filter: '',
			currentPage: 1,
			hv: 'dark',
			invoiceType: 1,
			poLists: [],
			selectedRows: [],
			isSelectedAll: false
		};
	},
	async mounted() {
		await this.initData();
	},
	computed: {
		poFields() {
			return [
				{
					key: 'select',
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'id',
					label: this.FormMSG(2, 'PO. no.'),
					formatter: (v) => `${v}`,
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'requestDate',
					label: this.FormMSG(3, 'Date'),
					formatter: (v) => formatTableDate(v),
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'supplierName',
					label: this.FormMSG(5, 'Supplier'),
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'description',
					label: this.FormMSG(6, 'Description'),
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'departmentName',
					label: this.FormMSG(7, 'Department'),
					formatter: (v) => v,
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff' }
				},
				{
					key: 'amountTotal',
					label: this.FormMSG(9, 'Initial Amount'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff' }
				},
				{
					key: 'remainingAmount',
					label: this.FormMSG(12, 'Remaining Amount'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff' }
				}
			];
		}
	},
	methods: {
		async initData() {
			try {
				// getPO data
				const filter = {
					status: VALIDATION_TYPE.VALIDATED_LEVEL_2,
					consumed: false
				};
				const rawDataResult = await getProjectUserPoDetail({ ...filter, groupBySheets: true }, 'no-cache');
				const result = rawDataResult.map((data) => data.purchaseOrder);
				// reinitialize selections
				if (this.selectedPo.length > 0) {
					const selectedID = this.selectedPo.map((e) => +e.id);
					this.selectedRows = result.filter((x) => selectedID.includes(+x.id)).map((x) => this.provideSelectionPayload(x));
					this.emitSelection();
				} else {
					this.selectedRows = [];
				}
				// initialize table data
				this.poLists = this.provideCleanPOs(result);
				if (this.poLists.length === this.selectedRows.length) this.isSelectedAll = true;
			} catch (error) {
				console.log(error);
			}
		},
		provideCleanPOs(records) {
			const reducedSelectedRows = this.selectedRows.map((x) => x.id);
			const newRecords = records.map((po) => {
				return {
					...po,
					selectState: reducedSelectedRows.includes(+po.id)
				};
			});

			return newRecords;
		},
		provideSelectionPayload(selected) {
			return {
				id: +selected.id,
				supplierId: +selected.supplierId,
				supplierName: selected.supplierName
			};
		},
		updateSelectedRows(payload, selected) {
			for (let i = 0; i < this.poLists.length; i++) {
				if (+this.poLists[i].id === +selected.id) {
					this.poLists[i].selectState = payload;
				}
			}

			const foundIndex = this.selectedRows.findIndex((x) => +x.id === +selected.id);
			if (foundIndex !== -1) {
				this.selectedRows.splice(foundIndex, 1);
			} else this.selectedRows.push(this.provideSelectionPayload(selected));

			this.isSelectedAll = this.selectedRows.length === this.poLists.length;

			this.emitSelection();
		},
		setSelectionForAll(payload) {
			this.poLists = this.poLists.map((po) => {
				return { ...po, selectState: payload };
			});

			if (payload === true) {
				for (const item of this.poLists) {
					this.selectedRows.push(this.provideSelectionPayload(item));
				}
			} else {
				this.selectedRows = [];
			}

			this.emitSelection();
		},
		emitSelection() {
			this.$emit('selection:change', this.selectedRows);
		}
	}
};
</script>

<style lang="scss" scoped>
:deep(.vertical-centered) {
	vertical-align: middle !important;
}
</style>
